/**
 * Routing-strategy endpoints for the RoutingDetails surface (Phase 1).
 *
 * Live wire (captured from portal.dev.ordermesh.io DevTools, rank-1):
 *   GET    /routing/v1/routing-strategies/merchant/{merchantId}  → { routingStrategies: RoutingStrategy[] }
 *   POST   /routing/v1/activate/{strategyId}   body { routingStrategyId }   (activate)
 *   DELETE /routing/v1/routing-strategies/{strategyId}                       (delete)
 *
 * Legacy parity (portalnew `store/sagas/routing.js`):
 *   - `doDeleteStrategy` re-fetches strategies after delete → modeled here as
 *     `invalidatesTags` on the merchant's strategy list.
 *   - `doActivateStrategy` toasts success; the active/available split must
 *     refresh so the newly-active strategy moves tables → also invalidates the
 *     list (RTK auto-refetch replaces the saga's manual reducer flip).
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';
import type {
  ActiveStrategiesResponse,
  ActiveStrategySummary,
  RoutingStrategiesResponse,
  RoutingStrategy,
  RoutingStrategyResponse,
  DataSet,
  DataSetsResponse,
  DataSetResponse,
  DataSetPayload,
  SystemVariable,
  SystemVariablesResponse,
} from './types';

/**
 * Exported request builders — single source of truth for each endpoint's
 * URL + method + body. `query:` hands these straight to RTK Query and the
 * wire-shape-lock test asserts them with `toEqual` (fetchBaseQuery binds
 * global fetch at module load, so asserting the builder is the portal2
 * convention — see `features/integration/api.phase5.test.ts`).
 */
export const routingRequestBuilders = {
  getStrategiesForMerchant: ({ merchantId }: { merchantId: string }) => ({
    url: `${services.routing}/v1/routing-strategies/merchant/${encodeURIComponent(merchantId)}`,
    method: 'GET' as const,
  }),
  getStrategy: ({ strategyId }: { strategyId: string }) => ({
    url: `${services.routing}/v1/routing-strategies/${encodeURIComponent(strategyId)}`,
    method: 'GET' as const,
  }),
  // merchantIds are sorted so equal sets produce one URL (and one RTK cache
  // entry) regardless of caller ordering. Same comparator as
  // ActiveStrategyCell's sort, so the two stay in step.
  getActiveStrategies: ({ merchantIds }: { merchantIds: string[] }) => ({
    url:
      `${services.routing}/v1/routing-strategies/active?` +
      [...merchantIds]
        .sort((a, b) => a.localeCompare(b))
        .map((id) => `merchantIds=${encodeURIComponent(id)}`)
        .join('&'),
    method: 'GET' as const,
  }),
  getDataSets: ({ strategyId }: { strategyId: string }) => ({
    url: `${services.routing}/v1/routing-strategies/${encodeURIComponent(strategyId)}/data-sets`,
    method: 'GET' as const,
  }),
  getDataSet: ({ strategyId, dataSetId }: { strategyId: string; dataSetId: string }) => ({
    url: `${services.routing}/v1/routing-strategies/${encodeURIComponent(strategyId)}/data-sets/${encodeURIComponent(dataSetId)}`,
    method: 'GET' as const,
  }),
  createDataSet: ({ strategyId, dataSet }: { strategyId: string; dataSet: DataSetPayload }) => ({
    url: `${services.routing}/v1/routing-strategies/${encodeURIComponent(strategyId)}/data-sets`,
    method: 'POST' as const,
    body: dataSet,
  }),
  updateDataSet: ({
    strategyId,
    dataSetId,
    dataSet,
  }: {
    strategyId: string;
    dataSetId: string;
    dataSet: DataSetPayload;
  }) => ({
    url: `${services.routing}/v1/routing-strategies/${encodeURIComponent(strategyId)}/data-sets/${encodeURIComponent(dataSetId)}`,
    method: 'PUT' as const,
    body: dataSet,
  }),
  deleteDataSet: ({ strategyId, dataSetId }: { strategyId: string; dataSetId: string }) => ({
    url: `${services.routing}/v1/routing-strategies/${encodeURIComponent(strategyId)}/data-sets/${encodeURIComponent(dataSetId)}`,
    method: 'DELETE' as const,
  }),
  getSystemVariables: () => ({
    url: `${services.routing}/v1/data-sets/system-variables`,
    method: 'GET' as const,
  }),
  activateStrategy: ({ strategyId }: { strategyId: string }) => ({
    url: `${services.routing}/v1/activate/${encodeURIComponent(strategyId)}`,
    method: 'POST' as const,
    body: { routingStrategyId: strategyId },
  }),
  deleteStrategy: ({ strategyId }: { strategyId: string }) => ({
    url: `${services.routing}/v1/routing-strategies/${encodeURIComponent(strategyId)}`,
    method: 'DELETE' as const,
  }),
  createStrategy: (routingStrategy: RoutingStrategy) => ({
    url: `${services.routing}/v1/routing-strategies`,
    method: 'POST' as const,
    body: routingStrategy,
  }),
  updateStrategy: (routingStrategy: RoutingStrategy) => ({
    url: `${services.routing}/v1/routing-strategies`,
    method: 'PUT' as const,
    body: routingStrategy,
  }),
};

/**
 * Unwrap the `{ routingStrategies }` envelope observed on live. Exported so
 * the response-envelope contract is locked by a direct unit test (tolerates
 * both the envelope and a bare array, defends a missing key).
 */
export function unwrapStrategies(
  response: RoutingStrategiesResponse | RoutingStrategy[],
): RoutingStrategy[] {
  if (Array.isArray(response)) return response;
  return response.routingStrategies ?? [];
}

/** Unwrap the `{ activeRoutingStrategies }` envelope (tolerates a bare array). */
export function unwrapActiveStrategies(
  response: ActiveStrategiesResponse | ActiveStrategySummary[],
): ActiveStrategySummary[] {
  if (Array.isArray(response)) return response;
  return response.activeRoutingStrategies ?? [];
}

/** Unwrap the single-strategy `{ routingStrategy }` envelope (detail GET). */
export function unwrapStrategy(
  response: RoutingStrategyResponse | RoutingStrategy,
): RoutingStrategy {
  if ('routingStrategy' in response) return response.routingStrategy;
  return response;
}

/** Unwrap the `{ dataSets }` envelope (tolerates a bare array). */
export function unwrapDataSets(response: DataSetsResponse | DataSet[]): DataSet[] {
  if (Array.isArray(response)) return response;
  return response.dataSets ?? [];
}

/** Unwrap the single-dataset `{ dataSet }` envelope (the GET that carries `values`). */
export function unwrapDataSet(response: DataSetResponse | DataSet): DataSet {
  if ('dataSet' in response) return response.dataSet;
  return response;
}

/** Unwrap the `{ variables }` envelope of the system-variables GET. */
export function unwrapSystemVariables(
  response: SystemVariablesResponse | SystemVariable[],
): SystemVariable[] {
  if (Array.isArray(response)) return response;
  return response.variables ?? [];
}

export const routingApi = createApi({
  reducerPath: 'routingApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['RoutingStrategies', 'RoutingStrategy', 'RoutingDataSets', 'RoutingDataSet'],
  endpoints: (builder) => ({
    /** GET strategies for a merchant; unwraps `{ routingStrategies }`. */
    getStrategiesForMerchant: builder.query<RoutingStrategy[], { merchantId: string }>({
      query: (args) => routingRequestBuilders.getStrategiesForMerchant(args),
      transformResponse: unwrapStrategies,
      providesTags: (_r, _e, { merchantId }) => [{ type: 'RoutingStrategies', id: merchantId }],
    }),
    /**
     * Bulk GET the active strategy per merchant (routing list view); unwraps
     * `{ activeRoutingStrategies }`. Tagged per merchant with the same
     * `RoutingStrategies` tag as the per-merchant list, so activate / delete /
     * create / update mutations automatically refetch the summaries too.
     */
    getActiveStrategies: builder.query<ActiveStrategySummary[], { merchantIds: string[] }>({
      query: (args) => routingRequestBuilders.getActiveStrategies(args),
      transformResponse: unwrapActiveStrategies,
      providesTags: (_r, _e, { merchantIds }) =>
        merchantIds.map((id) => ({ type: 'RoutingStrategies' as const, id })),
    }),
    /** GET a single strategy (full detail); unwraps `{ routingStrategy }`. */
    getStrategy: builder.query<RoutingStrategy, { strategyId: string }>({
      query: (args) => routingRequestBuilders.getStrategy(args),
      transformResponse: unwrapStrategy,
      providesTags: (_r, _e, { strategyId }) => [{ type: 'RoutingStrategy', id: strategyId }],
    }),
    /** GET the strategy's uploaded data sets; unwraps `{ dataSets }`. */
    getDataSets: builder.query<DataSet[], { strategyId: string }>({
      query: (args) => routingRequestBuilders.getDataSets(args),
      transformResponse: unwrapDataSets,
      providesTags: (_r, _e, { strategyId }) => [{ type: 'RoutingDataSets', id: strategyId }],
    }),
    /**
     * GET a single data set (carries `values`); unwraps `{ dataSet }`. Used by
     * the row Download action (→ CSV) and edit-mode hydration. `lazy` is the
     * Download path (fetch-on-click), the query hook the edit-page path.
     */
    getDataSet: builder.query<DataSet, { strategyId: string; dataSetId: string }>({
      query: (args) => routingRequestBuilders.getDataSet(args),
      transformResponse: unwrapDataSet,
      providesTags: (_r, _e, { dataSetId }) => [{ type: 'RoutingDataSet', id: dataSetId }],
    }),
    /**
     * GET system variables. Reproduces the legacy fire-on-CSV-parse call so the
     * network trace matches live; the result is intentionally unused (legacy
     * renders no UI from it — "Map To" is a hardcoded literal list).
     */
    getSystemVariables: builder.query<SystemVariable[], void>({
      query: () => routingRequestBuilders.getSystemVariables(),
      transformResponse: unwrapSystemVariables,
    }),
    /** POST a new data set; refetches the strategy's list (legacy `cb` refetch). */
    createDataSet: builder.mutation<void, { strategyId: string; dataSet: DataSetPayload }>({
      query: (args) => routingRequestBuilders.createDataSet(args),
      invalidatesTags: (_r, _e, { strategyId }) => [{ type: 'RoutingDataSets', id: strategyId }],
    }),
    /** PUT an existing data set; refetches the list + the single dataset. */
    updateDataSet: builder.mutation<
      void,
      { strategyId: string; dataSetId: string; dataSet: DataSetPayload }
    >({
      query: (args) => routingRequestBuilders.updateDataSet(args),
      invalidatesTags: (_r, _e, { strategyId, dataSetId }) => [
        { type: 'RoutingDataSets', id: strategyId },
        { type: 'RoutingDataSet', id: dataSetId },
      ],
    }),
    /** DELETE a data set; refetches the list (legacy re-fetches after delete). */
    deleteDataSet: builder.mutation<void, { strategyId: string; dataSetId: string }>({
      query: (args) => routingRequestBuilders.deleteDataSet(args),
      invalidatesTags: (_r, _e, { strategyId }) => [{ type: 'RoutingDataSets', id: strategyId }],
    }),
    /**
     * POST activate — invalidates the merchant's list so the active/available
     * split refreshes, AND every cached single strategy. The bare-type tag is
     * deliberate: activating one strategy implicitly deactivates the
     * previously-active one on the server, and this layer has no way to know
     * that strategy's id. A per-id invalidation left the old strategy's
     * cached `getStrategy` at `isActive: true`, so its detail page kept
     * showing "Active" after another strategy took over.
     */
    activateStrategy: builder.mutation<void, { strategyId: string; merchantId: string }>({
      query: ({ strategyId }) => routingRequestBuilders.activateStrategy({ strategyId }),
      invalidatesTags: (_r, _e, { merchantId }) => [
        { type: 'RoutingStrategies', id: merchantId },
        'RoutingStrategy',
      ],
    }),
    /** DELETE strategy — invalidates the merchant's list (legacy re-fetches after delete). */
    deleteStrategy: builder.mutation<void, { strategyId: string; merchantId: string }>({
      query: ({ strategyId }) => routingRequestBuilders.deleteStrategy({ strategyId }),
      invalidatesTags: (_r, _e, { merchantId }) => [{ type: 'RoutingStrategies', id: merchantId }],
    }),
    /**
     * POST a new strategy (full payload incl. assignedBlocks). The service
     * responds `{ routingStrategy }` (CreateRoutingStrategyResponse) — unwrap
     * it so callers can read the server-assigned id (the v3 guided-creation
     * flow navigates to it).
     */
    createStrategy: builder.mutation<RoutingStrategy, RoutingStrategy>({
      query: (routingStrategy) => routingRequestBuilders.createStrategy(routingStrategy),
      transformResponse: unwrapStrategy,
      invalidatesTags: (_r, _e, s) => [{ type: 'RoutingStrategies', id: s.merchantId }],
    }),
    /**
     * PUT an existing strategy (full payload incl. id + assignedBlocks). The
     * service responds `{ routingStrategy }` (UpdateRoutingStrategyResult) —
     * unwrap it so the v3 authoring shell can re-hydrate its draft from the
     * saved document without waiting for the tag-invalidation refetch.
     */
    updateStrategy: builder.mutation<RoutingStrategy, RoutingStrategy>({
      query: (routingStrategy) => routingRequestBuilders.updateStrategy(routingStrategy),
      transformResponse: unwrapStrategy,
      invalidatesTags: (_r, _e, s) => [
        { type: 'RoutingStrategies', id: s.merchantId },
        { type: 'RoutingStrategy', id: s.id },
      ],
    }),
  }),
});

export const {
  useGetStrategiesForMerchantQuery,
  useGetActiveStrategiesQuery,
  useGetStrategyQuery,
  useGetDataSetsQuery,
  useGetDataSetQuery,
  useLazyGetDataSetQuery,
  useGetSystemVariablesQuery,
  useLazyGetSystemVariablesQuery,
  useActivateStrategyMutation,
  useDeleteStrategyMutation,
  useCreateStrategyMutation,
  useUpdateStrategyMutation,
  useCreateDataSetMutation,
  useUpdateDataSetMutation,
  useDeleteDataSetMutation,
} = routingApi;
